import { useCallback, useEffect, useState } from 'react';
import { PeopleChatPanel } from '@rocket-technology-ltd/people-chat/client';
import { useAuth } from '../../auth';
import { useSocket } from '../../socket/core/Socket';
import { useWarehouse } from '../../../context/WarehouseContext';
import { CONFIGURATION_SETTING_KEYS } from '../../../../utils/configurationSettingKeys';
import { fetchConfigurationSettingByKey } from '../../../../utils/helper.method';
import { notifyByKey, playNotificationSound } from '../utils/sweet-alert';
import { chatApi, ChatPreferences, ChatPresenceEvent } from '../services/people-chat-service';
import type { ChatEvent } from '@rocket-technology-ltd/people-chat/client';

const configuredRolesAllowUser = (value: unknown, role: { reference?: unknown; _id?: unknown } | undefined) => {
  let configured: unknown[] = [];
  if (typeof value === 'string') configured = value.split(',');
  else if (Array.isArray(value)) configured = value;
  const allowed = new Set(configured.filter((id): id is string => typeof id === 'string').map((id) => id.trim()).filter(Boolean));
  return [role?.reference, role?._id]
    .filter((id): id is string => typeof id === 'string')
    .some((id) => allowed.has(id.trim()));
};

const PeopleChat = () => {
  const { currentUser } = useAuth();
  const { selectedWarehouse } = useWarehouse();
  const { socket } = useSocket();
  const scopeId = selectedWarehouse?.value || String(currentUser?.warehouse?.reference || '');
  const userId = currentUser?._id || '';
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    let isCurrent = true;
    setEnabled(false);
    if (!scopeId || !userId) return () => { isCurrent = false; };

    void fetchConfigurationSettingByKey(
      CONFIGURATION_SETTING_KEYS.KEY__PEOPLE_CHAT_ROLE_IDS,
      scopeId,
    ).then((setting) => {
      if (isCurrent) {
        setEnabled(configuredRolesAllowUser(
          setting?.value ?? setting?.defaultValue,
          currentUser?.roleAndPermission,
        ));
      }
    }).catch(() => {
      if (isCurrent) setEnabled(false);
    });

    return () => { isCurrent = false; };
  }, [currentUser?._id, currentUser?.roleAndPermission, scopeId, userId]);

  const subscribe = useCallback((onMessage: (event: ChatEvent) => void, onReconnect: () => void,
    onPresence: (event: ChatPresenceEvent) => void) => {
    if (!socket) return () => {};
    socket.on('onChatMessage', onMessage);
    socket.on('connect', onReconnect);
    socket.on('onChatPresence', onPresence);
    return () => {
      socket.off('onChatMessage', onMessage);
      socket.off('connect', onReconnect);
      socket.off('onChatPresence', onPresence);
    };
  }, [socket]);

  const notify = useCallback((sender: string, preview: string, preferences: ChatPreferences) => {
    if (preferences.sound) playNotificationSound(CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_PEOPLE_CHAT);
    if (preferences.popup) {
      void notifyByKey(CONFIGURATION_SETTING_KEYS.KEY__NOTIFICATION_PEOPLE_CHAT,
        ['info', sender, preview], false).catch(() => undefined);
    }
  }, []);

  if (!scopeId || !userId) return null;

  return <PeopleChatPanel
    scopeId={scopeId}
    userId={userId}
    enabled={enabled}
    api={chatApi}
    subscribe={subscribe}
    notify={notify}
    connected={Boolean(socket?.connected)}
  />;
};

export default PeopleChat;
